import Papa from 'papaparse';
import type { PapaParsePassthrough } from '../types/template';

export type CsvRow = Record<string, string>;

export type CsvParseResult = {
  rows: CsvRow[];
  structuralErrors: CsvRow[];
  dataRowCount: number;
  structuralErrorCount: number;
};

const STRUCTURAL_ERRORS_CAP = 1000;

/**
 * Parses a CSV file via PapaParse. Focuses on parsing + structural error
 * detection only — classification is the controller's job via `validateRows`.
 *
 * Arrays are capped for memory; counters are true counts for reporting.
 */
export async function parseCSV(
  file: File,
  options: PapaParsePassthrough,
  maxRows: number,
  signal?: AbortSignal,
): Promise<CsvParseResult> {
  if (signal?.aborted) {
    return {
      rows: [],
      structuralErrors: [],
      dataRowCount: 0,
      structuralErrorCount: 0,
    };
  }

  return new Promise<CsvParseResult>((resolve, reject) => {
    const rows: CsvRow[] = [];
    const structuralErrors: CsvRow[] = [];
    let dataRowCount = 0;
    let structuralErrorCount = 0;

    const result = () => ({
      rows,
      structuralErrors,
      dataRowCount,
      structuralErrorCount,
    });

    const onAbort = () => resolve(result());
    signal?.addEventListener('abort', onAbort, { once: true });

    Papa.parse<CsvRow>(file, {
      skipEmptyLines: true,
      ...options,
      header: true,
      //TODO create a dedicate workfe file that imports papaparse and handles the parsing , then communicates via postMessage.
      worker: false,

      step: ({ data }) => {
        if (signal?.aborted) return;

        if ('__parsed_extra' in data) {
          structuralErrorCount++;
          if (structuralErrors.length < STRUCTURAL_ERRORS_CAP)
            structuralErrors.push(data);
          return;
        }

        dataRowCount++;
        if (dataRowCount <= maxRows) rows.push(data);
      },

      complete: () => {
        signal?.removeEventListener('abort', onAbort);
        resolve(result());
      },

      error: (err) => {
        signal?.removeEventListener('abort', onAbort);
        reject(new Error(err.message));
      },
    });
  });
}
